import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-brand-400 text-gray-950 hover:bg-brand-300 active:bg-brand-500 glow-brand-sm hover:glow-brand',
        destructive: 'bg-red-500 text-white hover:bg-red-400 active:bg-red-600',
        outline: 'text-white', // Border handled by notched compound variant
        secondary: 'bg-gray-800 text-white hover:bg-gray-700 active:bg-gray-900',
        ghost: 'text-gray-300 hover:bg-white/[0.05] hover:text-white',
        link: 'text-brand-400 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-12 px-8 text-base',
        icon: 'h-10 w-10',
      },
      notched: {
        true: '', // Applied via compound variants based on variant type
        false: 'rounded-lg',
      },
    },
    compoundVariants: [
      // Solid fill variants - use clip-path directly (no border needed)
      { variant: 'default', notched: true, class: 'clip-notched' },
      { variant: 'destructive', notched: true, class: 'clip-notched' },
      { variant: 'secondary', notched: true, class: 'clip-notched' },
      { variant: 'ghost', notched: true, class: 'clip-notched' },
      // Outline variant - use pseudo-element border that follows clip-path
      { variant: 'outline', notched: true, class: 'notch-border notch-border-white/12 notch-bg-white/3 notch-border-hover notch-bg-hover' },
      // Outline with rounded (no notch) - use regular border
      { variant: 'outline', notched: false, class: 'border border-white/[0.12] bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/[0.2]' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      notched: true,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, notched, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, notched, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
